// Util functions to be used by our openapi-related repository parser, never
// to be used directly by our components.
import { OpenAPIV3 } from "openapi-types";
import { monitoring } from "@/lib/integrations/index.js";
import { get as lodashGet } from "lodash";

export interface IDetail {
    loc: string[];
    msg: string;
    type: string;
}

export interface IFormErrorVisualizerData {
    response: Response;
    json: IStekzBackendJSONResponse;
}

export interface IStekzBackendJSONResponse {
    errors?: string[];
    warnings?: string[];
    // This one is a bit weird, but for field-specific errors 'detail' contains
    // a list of objects, but when e.g. we are not authenticated, it contains
    // one string.
    detail?: IDetail[] | string;
}

async function fetchApiSpec(url: string): Promise<OpenAPIV3.Document> {
    let response: Response;
    try {
        response = await fetch(url, {
            mode: "cors",
        });
    } catch (e) {
        if (e instanceof TypeError) {
            monitoring.error(`Cannot fetch the openapi spec from '${url}'.`);
        }
        throw e;
    }
    return response.json();
}

const specCache = {};
// retrieve an openapi spec from a given url. Typically they are around
// 100 kb, which is quite big. To avoid re-fetching every time this
// function is called, there is a simple caching mechanism in place.
export function getApiSpec(url: string): Promise<OpenAPIV3.Document> {
    // When an api spec is already fetched or being fetched, return a reference
    // to the promise of that fetch instead.
    if (url in specCache) {
        return specCache[url];
    }
    specCache[url] = fetchApiSpec(url);
    return specCache[url];
}

// Extract the json response content from a certain request
export function getPathResponseContent(
    apiSpec: OpenAPIV3.Document,
    url: string,
    action: string
) {
    const statusCodesArr = ["200", "201"];
    const availableStatusCode = statusCodesArr.find((statusCode) => {
        return statusCode in apiSpec.paths[url][action].responses;
    });
    if (!availableStatusCode) {
        monitoring.warning(
            `Schema responses other than '200' and '201' are not supported yet.`
        );
        return undefined;
    }

    const responseContent =
        apiSpec?.paths?.[url]?.[action]?.responses?.[availableStatusCode]
            ?.content;

    if (!responseContent) {
        return undefined;
    }

    if ("application/json" in responseContent) {
        return responseContent["application/json"];
    } else if ("application/x-www-form-urlencoded" in responseContent) {
        return responseContent["application/x-www-form-urlencoded"];
    } else {
        monitoring.error(
            `Cannot parse non-'application/json' and non-'application/x-www-form-urlencoded' types yet. (${url})`
        );
        return undefined;
    }
}

export function getSchemaFromSchemaLocation(
    apiSpec: OpenAPIV3.Document,
    schemaLocation: string
): OpenAPIV3.SchemaObject {
    // Turn e.g. #/components/schemas/ProducerCreate into
    // [ 'components', 'schemas', 'ProducerCreate' ] into
    // components.schemas.ProducerCreate
    const locator = schemaLocation.split("/").slice(1).join(".");
    // Get the schema from the nested object using the extracted list.
    const schema: OpenAPIV3.SchemaObject = lodashGet(apiSpec, locator);
    if (schema === undefined) {
        monitoring.error(`Cannot find ${locator} in the api spec`);
        return undefined;
    }

    return schema;
}

export function getResponseSchemaForAction(apiSpec, url, action) {
    const pathResponseContent = getPathResponseContent(apiSpec, url, action);
    if (!pathResponseContent) {
        monitoring.warning(
            `No response found in the '${action}' method on '${url}'`
        );
        return undefined;
    }

    if (
        !("schema" in pathResponseContent) ||
        Object.keys(pathResponseContent.schema).length === 0
    ) {
        monitoring.warning(
            `No schema found in the response of the '${action}' method on '${url}'`
        );
        return undefined;
    }
    let schema = pathResponseContent.schema;

    if (
        "items" in pathResponseContent.schema &&
        "$ref" in pathResponseContent.schema.items
    ) {
        schema = pathResponseContent.schema.items;
    }
    return schema;
}

export function getSchemaForMethod(
    apiSpec: OpenAPIV3.Document,
    url: string,
    method: string
): OpenAPIV3.BaseSchemaObject {
    let schema = undefined;

    if (method === "post" || method === "put" || method === "patch") {
        // There is not always a request body in a POST/PUT/PATCH, in that case,
        // we return undefined;
        if (!("requestBody" in apiSpec.paths[url][method])) {
            return undefined;
        }

        const requestBodyContent =
            apiSpec?.paths?.[url]?.[method]?.requestBody?.content;

        if (!requestBodyContent) {
            return undefined;
        }

        if ("application/json" in requestBodyContent) {
            schema = requestBodyContent["application/json"].schema;
        } else if ("application/x-www-form-urlencoded" in requestBodyContent) {
            schema =
                requestBodyContent["application/x-www-form-urlencoded"].schema;
        } else if ("multipart/form-data" in requestBodyContent) {
            schema = requestBodyContent["multipart/form-data"].schema;
        } else {
            if (Object.keys(requestBodyContent).length > 0) {
                monitoring.error(
                    `Can't parse "${Object.keys(requestBodyContent)[0]}" yet. (${url})`
                );
            } else {
                monitoring.error(
                    `Can only parse the following types:\n - application/json\n - application/x-www-form-urlencoded\n - multipart/form-data\n(${url})`
                );
            }
            return undefined;
        }
        // There is no request body in a GET. Any properties associated with a 'GET'
        // method on repositories return the response schema.
    } else if (method === "get") {
        schema = getResponseSchemaForAction(apiSpec, url, method);
    }

    if (!schema) {
        return undefined;
    }

    if ("$ref" in schema) {
        return getSchemaFromSchemaLocation(apiSpec, schema.$ref);
    }

    return schema;
}
